import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    ExcelData, 
    CitySums, 
    CityTerminal, 
    CompareFiles, 
    SnackbarState 
} from './types';
import { 
    readExcelFile, 
    calculateSums, 
    matchAndUpdateData, 
    exportToExcel 
} from './utils';

export const useCompare = () => {
    const [salesReport, setSalesReport] = useState<string>('');
    const [archiveOrders, setArchiveOrders] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [processedOrders, setProcessedOrders] = useState<number>(0);
    const [matchedOrders, setMatchedOrders] = useState<number>(0);
    const [selectedDate, setSelectedDate] = useState('');
    const [processedData, setProcessedData] = useState<ExcelData | null>(null);
    const [isProcessed, setIsProcessed] = useState(false);
    const [sums, setSums] = useState<CitySums>({ total: 0 });
    const [citySums, setCitySums] = useState<CitySums>({ total: 0 });
    const [files, setFiles] = useState<CompareFiles>({
        salesReport: null,
        archiveOrders: null
    });
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [openCityManager, setOpenCityManager] = useState(false);
    const [cityTerminals, setCityTerminals] = useState<CityTerminal[]>([]);

    useEffect(() => {
        document.title = 'Сопоставление Excel файлов | Zerde';
        
        const fetchCityTerminals = async () => {
            try {
                const response = await axios.get('https://tbl.zerdegroup.kz/api/city-terminals');
                if (response.data.success) {
                    setCityTerminals(response.data.terminals);
                } else {
                    showNotification('Ошибка при загрузке данных о городах', 'error');
                }
            } catch (error) {
                console.error('Error fetching city terminals:', error);
                showNotification('Ошибка при загрузке данных о городах', 'error');
            }
        };
        
        fetchCityTerminals();
    }, []);

    const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const fetchPharmacyDeliveryData = async (date: string) => {
        setIsLoading(true);

        try {
            const response = await axios.get(`https://tbl.zerdegroup.kz/api/pharmacy-delivery-date/${date}`);
            if (response.data.success) {
                const newCitySums: CitySums = {
                    total: response.data.data.totalSum || 0
                };

                if (response.data.data.citySums) {
                    // Ключи теперь - это created_by (аптеки), а не города
                    Object.entries(response.data.data.citySums).forEach(([pharmacyKey, sum]) => {
                        newCitySums[pharmacyKey] = Number(sum) || 0;
                    });
                }

                setCitySums(newCitySums);
            } else {
                showNotification(response.data.message, 'error');
            }
        } catch (error) {
            console.error('Error fetching pharmacy delivery data:', error);
            showNotification('Произошла ошибка при получении данных', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
        setFile: React.Dispatch<React.SetStateAction<string>>,
        fileType: 'salesReport' | 'archiveOrders'
    ) => {
        const file = event.target.files?.[0];
        if (file) {
            setFile(file.name);
            setFiles(prev => ({ ...prev, [fileType]: file }));

            if (fileType === 'salesReport') {
                try {
                    const salesReportData = await readExcelFile(file, 'salesReport');
                    setSums(calculateSums(salesReportData, null, cityTerminals));
                } catch (error) {
                    console.error('Error calculating initial sums:', error);
                }
            }
        }

        event.target.value = '';
    };

    const clearFile = (
        inputId: string,
        setFile: React.Dispatch<React.SetStateAction<string>>,
        fileType: 'salesReport' | 'archiveOrders'
    ) => {
        setFile('');
        setFiles(prev => ({ ...prev, [fileType]: null }));
        setSums({ total: 0 });
        const input = document.getElementById(inputId) as HTMLInputElement;
        if (input) {
            input.value = '';
        }
    };

    const handleStart = async () => {
        if (!files.salesReport || !files.archiveOrders) return;

        setIsLoading(true);
        setProcessedOrders(0);
        setMatchedOrders(0);
        setIsProcessed(false);

        try {
            const [salesReportData, archiveOrdersData] = await Promise.all([
                readExcelFile(files.salesReport, 'salesReport'),
                readExcelFile(files.archiveOrders, 'archiveOrders')
            ]);

            const { data: updatedSalesReport, stats } = matchAndUpdateData(
                salesReportData, 
                archiveOrdersData, 
                cityTerminals
            );

            setProcessedData(updatedSalesReport);
            setProcessedOrders(stats.processed);
            setMatchedOrders(stats.matched);
            setSums(calculateSums(updatedSalesReport, archiveOrdersData, cityTerminals));
            setIsProcessed(true);

            showNotification(
                `Обработка завершена!\nОбработано заказов: ${stats.processed}\nВсего совпадений: ${stats.matched}\n` +
                `Через склад КД: ${stats.warehouseMatched}\nЧерез адрес: ${stats.addressMatched}\nНе найдено: ${stats.notMatched}`,
                'success'
            );
        } catch (error) {
            console.error('Error processing files:', error);
            showNotification(`Произошла ошибка при обработке файлов`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (processedData && files.salesReport) {
            exportToExcel(processedData, files.salesReport);
        }
    };

    const handleClearAll = () => {
        clearFile('salesReport', setSalesReport, 'salesReport');
        clearFile('archiveOrders', setArchiveOrders, 'archiveOrders');
        setIsProcessed(false);
        setProcessedData(null);
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedDate = e.target.value;
        setSelectedDate(selectedDate);
        fetchPharmacyDeliveryData(selectedDate);
    };

    return {
        // State
        salesReport,
        archiveOrders,
        isLoading,
        processedOrders,
        matchedOrders,
        selectedDate,
        processedData,
        isProcessed,
        sums,
        citySums,
        files,
        snackbar,
        openCityManager,
        cityTerminals,
        
        // Actions
        setSalesReport,
        setArchiveOrders,
        setOpenCityManager,
        showNotification,
        handleCloseSnackbar,
        handleFileChange,
        clearFile,
        handleStart,
        handleDownload,
        handleClearAll,
        handleDateChange
    };
};
